import React, { useEffect, useState } from 'react';
import i18next from 'i18next';
import { useSelector } from 'react-redux';

import { Button, Color, Size } from '@signaturit/react-components-library';

import { canShowOfflinePaymentButton, getColorMap } from './ManageCreditsHelper';

import { ProductTypes } from '../../constants/ProductTypes';
import { selectUserInfo } from '../../store/User/reducer';
import { UserRole } from '../../constants/UserRole';
import { LicenseCodename } from '../../constants/LicenseCodename';

import { DataTrackerEvents } from '../../dataTracker/DataTrackerEvents';
import { sendDataTrackerEventHandler } from '../../../main/dataTracker/DataTrackerHelper';
import { User } from '../../../models/User';
import { AcceptedExtraData } from '@signaturit/data-tracker-client';
import { WheelColor } from '../Gauges/WheelContainer/WheelColor.enum';

export interface IManageProps {
    type: string;
    colorClassLabel?: WheelColor;
}

export const canShowManageCreditsButton = (userInfo: User): boolean => {
    const isNotAdmin = userInfo.role !== UserRole.admin;
    const isEnterprise = userInfo.codename === LicenseCodename.enterprise;
    const isTrial = userInfo.notifications && userInfo.notifications.isTrial;
    const isDemo = userInfo.codename === LicenseCodename.demo;

    if (isNotAdmin) {
        return false;
    }
    if (isEnterprise) {
        return false;
    }
    if (isTrial) {
        return false;
    }
    if (isDemo) {
        return false;
    }

    return true;
};
export const canShowUpgradeAccountButton = (userInfo: User): boolean => {
    const isNotAdmin = userInfo.role !== UserRole.admin;
    const isDemo = userInfo.codename === LicenseCodename.demo;
    const isTrial = userInfo.notifications && userInfo.notifications.isTrial && !userInfo.adHoc;

    if (isNotAdmin) {
        return false;
    }
    if (isDemo) {
        return true;
    }
    if (isTrial) {
        return true;
    }
    return false;
};

export const ManageCredits = ({ type, colorClassLabel = WheelColor.full }: IManageProps) => {
    let productType = type;
    const mappedButtonColor:
        | Color.primary
        | Color.secondary
        | Color.tertiary
        | Color.warning
        | Color.error = getColorMap(colorClassLabel);
    const MANAGE_URL = '#/billing/update';
    const userInfo = useSelector(selectUserInfo);
    const [canManageCredits, setCanManageCredits] = useState<boolean>(false);
    const [canUpgradeAccount, setCanUpgradeAccount] = useState(false);
    const [isOfflinePayment, setIsOfflinePayment] = useState<boolean>(false);
    const EMAIL_LINK = 'mailto:petitions@signaturit.com';
    const UPGRADE_LINK = '#/settings/upgrade';

    useEffect(() => {
        const canShowButton = userInfo && type !== ProductTypes.Batch;
        if (canShowButton) {
            const showManageCreditsButton = canShowManageCreditsButton(userInfo);
            const showOfflinePaymentButton = canShowOfflinePaymentButton(userInfo);
            const showUpgradeAccountButton = canShowUpgradeAccountButton(userInfo);
            if (showUpgradeAccountButton) {
                setCanUpgradeAccount(showUpgradeAccountButton);
            } else if (showOfflinePaymentButton) {
                setIsOfflinePayment(showOfflinePaymentButton);
            } else if (showManageCreditsButton) {
                setCanManageCredits(showManageCreditsButton);
            }
        }
    }, [userInfo]);

    const onClickGauges = () => {
        const licenses = userInfo?.codename;
        const trackerData = { licenses, productType };
        sendDataTrackerEventHandler(DataTrackerEvents.clickNotificationButtonManage, trackerData, [
            AcceptedExtraData.limits,
        ]);
        window.location.hash = `${MANAGE_URL}?creditType=${productType}`;
    };

    return (
        <div>
            {canManageCredits && (
                <Button onClick={onClickGauges} color={mappedButtonColor} size={Size.small}>
                    {i18next.t('default.gauges._popup.button_manage_credits')}
                </Button>
            )}
            {isOfflinePayment && (
                <Button
                    onClick={() => {
                        const licenses = userInfo?.codename;
                        const trackerData = { licenses, productType };
                        sendDataTrackerEventHandler(DataTrackerEvents.clickNotificationButtonContact, trackerData, [
                            AcceptedExtraData.limits,
                        ]);
                        location.href = EMAIL_LINK;
                    }}
                    color={mappedButtonColor}
                    size={Size.small}
                >
                    {i18next.t('default.gauges._popup.button')}
                </Button>
            )}
            {canUpgradeAccount && (
                <Button
                    onClick={() => {
                        const licenses = userInfo?.codename;
                        const trackerData = { licenses, productType };
                        sendDataTrackerEventHandler(DataTrackerEvents.clickNotificationButtonUpgrade, trackerData, [
                            AcceptedExtraData.limits,
                        ]);
                        location.href = UPGRADE_LINK;
                    }}
                    color={mappedButtonColor}
                    size={Size.small}
                >
                    {i18next.t('default.gauges._popup.button_upgrade_account')}
                </Button>
            )}
        </div>
    );
};
